import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import Tooltip from '@mui/material/Tooltip';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import StyledWrapper from '../../../IndividualProgram/style';

import { setFormMetaInfo } from '../../../../components/FormBuilderLibrary/slice';

import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import BasicCellText from './BasicCellText';
import ActionButton from './ActionButton';
import TableTitle from '../../../../components/TableTitle/TableTitle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IconButton from '@mui/material/IconButton';
import usePermissions from '../../../../hooks/usePermissions';

export const ProcessColumns = (columnWidths, openTooltip, setOpenTooltip, setWarningModal) => {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [finalColumns, setFinalColumns] = useState([]);
	const { hasEditPermissions } = usePermissions();
	const appProcessState = useRef();
	appProcessState.current = hasEditPermissions;

	useEffect(() => {
		appProcessState.current = hasEditPermissions;

		setFinalColumns([
			{
				field: 'Entity',
				headerName: 'Entity',
				renderCell: (cellValues) => {
					const apiRef = useGridApiContext();

					const row = cellValues.api?.getRow(cellValues.id);
					const { status, uniqueIdentifier: displayId, title, appId, type } = row;
					const hasChildren = cellValues.rowNode.children?.length > 0;
					const hasParent = cellValues.rowNode.parent != null;
					const titleData = {
						displayId,
						title,
						status,
						entityType: type,
						hasParent,
						hasChildren,
						row_id: row.id,
						expanded: cellValues.rowNode.childrenExpanded,
						isBlueprint: true
					};
					const paddingLeft = (cellValues.row?.hierarchy?.length - 1) * 28;
					const tableStyle = { paddingLeft: paddingLeft };
					const handleExpand = (row_id, expanded) => {
						apiRef.current.setRowChildrenExpansion(row_id, !expanded);
					};
					const handleCardClick = () => {
						dispatch(setFormMetaInfo({ id: cellValues.formattedValue }));

						switch (type) {
							case 'Submission Stage':
								setSearchParams({
									...searchParams,
									stageCreation: 'application',
									stageId: appId
								});
								break;
							case 'APPLICATION PROCESS':
								setSearchParams({
									...searchParams,
									stageCreation: 'process',
									processId: appId
								});
								break;
							case 'Evaluation Stage':
								setSearchParams({
									...searchParams,
									stageCreation: 'evaluation',
									stageId: appId
								});
								break;
							case 'Application Form':
								setSearchParams({
									...searchParams,
									formBuilder: 'application',
									formId: appId
								});
								break;
							case 'Review Form':
								setSearchParams({
									...searchParams,
									formBuilder: 'review',
									formId: appId
								});
								break;
						}
					};

					const isSubmission = type?.toLowerCase().includes('submission stage');
					const isAppProcess = type?.toLowerCase().includes('application process');

					return (
						<>
							{isSubmission ? (
								<StyledWrapper.BlockIndicator>
									{row?.submissionStageCount}
									<KeyboardDoubleArrowDownIcon sx={{ height: '20px' }} />
								</StyledWrapper.BlockIndicator>
							) : (
								!isAppProcess && <StyledWrapper.BlockVerticalLine />
							)}
							<TableTitle
								titleData={titleData}
								handleCardClick={handleCardClick}
								style={tableStyle}
								handleExpand={handleExpand}
							/>
						</>
					);
				},
				minWidth: 350,
				flex: !columnWidths.__tree_data_group__ && 3
			},
			{
				field: 'type',
				headerName: 'Type',
				description: 'Type',
				renderCell: (cellValues) => (
					<BasicCellText
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
					/>
				),
				minWidth: 200,
				flex: !columnWidths.title && 2
			},
			{
				field: 'startDate',
				headerName: 'Start',
				description: 'Start Date',
				renderCell: (cellValues) => (
					<BasicCellText
						isDate
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
					/>
				),
				minWidth: 150,
				flex: !columnWidths.startDate && 1
			},
			{
				field: 'endDate',
				headerName: 'End',
				description: 'End Date',
				renderCell: (cellValues) => (
					<BasicCellText
						isDate
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
					/>
				),
				minWidth: 150,
				flex: !columnWidths.endDate && 1
			},
			{
				field: 'action',
				headerName: hasEditPermissions ? 'Usage & Actions' : 'Usage',
				description: hasEditPermissions ? 'Usage & Actions' : 'Usage',
				renderCell: (cellValues) => {
					let hasSubmissionStage = false;
					let hasAppProcess = false;
					let last_stage = null;

					cellValues?.api?.getRowModels().forEach((value) => {
						if (value?.type === 'Submission Stage') {
							hasSubmissionStage = true;
						} else if (value?.type === 'APPLICATION PROCESS' && value?.appId) {
							hasAppProcess = true;
						}

						if (['Submission Stage', 'Evaluation Stage'].includes(value?.type)) {
							last_stage = value;
						}
					});

					const isCoreInfoForm = cellValues?.row?.purpose === 'Core Information';
					const isReadyToPublish = cellValues.row.isReadyToPublish;
					const isRowCreatedBeforePublication = cellValues?.row.createdBeforePublication;
					const isRowApplicationProcess = cellValues?.row.type === 'APPLICATION PROCESS';

					const isDisabled =
						isCoreInfoForm ||
						(isReadyToPublish && (isRowCreatedBeforePublication || isRowApplicationProcess));

					const isApplicationProcessPublished =
						cellValues.row.isReadyToPublish && cellValues?.row.type == 'APPLICATION PROCESS';
					return (
						hasAppProcess && (
							<div style={{ display: 'flex', gap: '5px' }}>
								{hasEditPermissions && (
									<Tooltip
										arrow
										placement="top"
										title={
											isCoreInfoForm
												? 'Core Information Form is mandatory and cannot be deleted.'
												: 'Delete'
										}
									>
										<IconButton
											aria-label="delete"
											disabled={isDisabled}
											onClick={() => {
												setSearchParams({
													...searchParams,
													deleteModal: cellValues?.row?.appId,
													hasCoreInfo: cellValues?.row?.hasCoreInfo,
													type: cellValues?.row?.type?.includes('APPLICATION PROCESS')
														? 'process'
														: cellValues?.row?.type?.includes('Stage')
														? 'stage'
														: 'form'
												});
											}}
										>
											<DeleteForeverIcon color={isDisabled ? 'action' : 'error'} />
										</IconButton>
									</Tooltip>
								)}

								{!isApplicationProcessPublished && hasEditPermissions && (
									<ActionButton
										isReadyToPublish={isReadyToPublish}
										type={cellValues?.row?.type}
										hasSubmissionStage={hasSubmissionStage}
										clickedRow={cellValues?.row}
										last_stage={last_stage}
										setWarningModal={setWarningModal}
									/>
								)}
							</div>
						)
					);
				},
				minWidth: 300
			}
		]);
	}, [hasEditPermissions]);

	return finalColumns;
};
